import {
    Add,
    Assignment, FileDownloadDoneOutlined,
    InsertDriveFileOutlined,
    Inventory2, SwapHoriz,
    TaskAlt,
    TrendingUp
} from "@mui/icons-material";
import { Skeleton, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Module } from "../../@types/DRO/GetModuleApiResponse";
import { DashboardApi, DashboardSummary, RecentOrder } from "../../api/Dashboard/DashboardApi";
import { GetModulesApi } from "../../api/GetModules/GetModules";
import MetricCard from "./MetricCard";


const statusStyles: Record<string, string> = {
    APPROVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
    CLOSED: "border-blue-200 bg-blue-50 text-blue-700",
    CREATED: "border-amber-200 bg-amber-50 text-amber-700",
    CANCELLED: "border-red-200 bg-red-50 text-red-700",
};

const chartColors = ["#2563eb", "#10b981", "#f59e0b"];

const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "--";
    return `${value > 0 ? "+" : ""}${value}%`;
};

const formatAmount = (value: number | null | undefined) =>
    value === null || value === undefined || Number.isNaN(value)
        ? "--"
        : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(value);

const DashboardSkeleton = () => (
    <div aria-label="Loading dashboard content" aria-busy="true">
        <section aria-hidden="true" className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }, (_, index) => (
                <article key={index} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                        <Skeleton animation="wave" variant="text" width="58%" height={20} />
                        <Skeleton animation="wave" variant="rounded" width={36} height={36} />
                    </div>
                    <Skeleton animation="wave" variant="text" width="45%" height={38} sx={{ mt: 1 }} />
                    <Skeleton animation="wave" variant="text" width="78%" height={18} sx={{ mt: 0.5 }} />
                </article>
            ))}
        </section>

        <section aria-hidden="true" className="mt-5 grid gap-4 lg:grid-cols-5">
            <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-3">
                <Skeleton animation="wave" variant="text" width={240} height={24} />
                <div className="mt-3 flex flex-col items-center gap-5 sm:flex-row sm:justify-start">
                    <div className="flex h-36 w-36 shrink-0 items-center justify-center">
                        <Skeleton animation="wave" variant="circular" width={128} height={128} />
                    </div>
                    <div className="w-full space-y-2 sm:w-40">
                        {Array.from({ length: 3 }, (_, index) => (
                            <Skeleton key={index} animation="wave" variant="text" width={`${80 + index * 5}%`} height={20} />
                        ))}
                    </div>
                </div>
            </article>
            <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
                <Skeleton animation="wave" variant="text" width={150} height={24} />
                <div className="mt-3 space-y-2">
                    {Array.from({ length: 3 }, (_, index) => (
                        <Skeleton key={index} animation="wave" variant="rounded" width="100%" height={36} />
                    ))}
                </div>
            </article>
        </section>

        <section aria-hidden="true" className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center border-b border-slate-200 px-4 py-4">
                <Skeleton animation="wave" variant="text" width={205} height={24} />
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-[760px] w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                        <tr>
                            {["Order", "Type", "Vendor / Customer", "Date", "Status", "Amount"].map((heading) => (
                                <th key={heading} className="px-4 py-3">{heading}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {Array.from({ length: 5 }, (_, rowIndex) => (
                            <tr key={rowIndex}>
                                {Array.from({ length: 6 }, (_, cellIndex) => (
                                    <td key={cellIndex} className="px-4 py-3">
                                        <Skeleton animation="wave" variant="text" width={cellIndex === 2 ? 150 : 90} height={20} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    </div>
);

function ERPDashboard() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [allSummary, setAllSummary] = useState<DashboardSummary | null>(null);
    const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
    const [allRecentOrders, setAllRecentOrders] = useState<RecentOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modules, setModules] = useState<Module[]>([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const ordersRequestId = useRef(0);
    const selectedToDate = useRef("");

    useEffect(() => {
        let isMounted = true;
        const initialOrdersRequestId = ++ordersRequestId.current;
        const fetchDashboardData = async () => {
            try {
                const [summaryResponse, recentOrdersResponse, modulesResponse] = await Promise.all([
                    DashboardApi.api.getDashboardSummary(),
                    DashboardApi.api.getRecentOrders(),
                    GetModulesApi.api.getModule(),
                ]);
                if (isMounted) {
                    const dashboardSummary = summaryResponse.data ?? null;
                    setAllSummary(dashboardSummary);
                    if (!selectedToDate.current) setSummary(dashboardSummary);
                    const orders = Array.isArray(recentOrdersResponse.data) ? recentOrdersResponse.data : [];
                    setAllRecentOrders(orders);
                    if (ordersRequestId.current === initialOrdersRequestId || !selectedToDate.current) {
                        setRecentOrders(orders);
                    }
                    setModules(modulesResponse.data?.userModuleDtoList ?? []);
                }
            } catch (requestError) {
                console.error("Error loading dashboard", requestError);
                if (isMounted) setError("Unable to load dashboard data.");
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setOrdersLoading(false);
                }
            }
        };

        fetchDashboardData();
        return () => { isMounted = false; };
    }, []);

    const fetchFilteredDashboard = async (startDate: string, endDate: string) => {
        const requestId = ++ordersRequestId.current;
        setOrdersLoading(true);
        setError(null);
        try {
            const [summaryResponse, ordersResponse] = await Promise.all([
                DashboardApi.api.getDashboardSummary(startDate || undefined, endDate),
                DashboardApi.api.getRecentOrders(startDate || undefined, endDate),
            ]);
            if (ordersRequestId.current === requestId) {
                setSummary(summaryResponse.data ?? null);
                setRecentOrders(Array.isArray(ordersResponse.data) ? ordersResponse.data : []);
            }
        } catch (requestError) {
            console.error("Error loading filtered dashboard data", requestError);
            if (ordersRequestId.current === requestId) setError("Unable to load dashboard data.");
        } finally {
            if (ordersRequestId.current === requestId) setOrdersLoading(false);
        }
    };

    const handleFromDateChange = (value: string) => {
        setFromDate(value);
        if (toDate) void fetchFilteredDashboard(value, toDate);
    };

    const handleToDateChange = (value: string) => {
        setToDate(value);
        selectedToDate.current = value;
        if (value) {
            void fetchFilteredDashboard(fromDate, value);
        } else {
            ordersRequestId.current += 1;
            setError(null);
            setSummary(allSummary);
            setRecentOrders(allRecentOrders);
            setOrdersLoading(false);
        }
    };

    const hasModuleKey = (key: string) =>
        modules.some((m) => m.key === key || m.subModuleList?.some((sub) => sub.key === key));

    const canTransferInventory = hasModuleKey("TRANSFER_ORDER");

    const metrics = summary ? [
        { label: summary.totalOrders?.title ?? "Total Orders", value: String(summary.totalOrders?.count ?? 0), trend: formatPercentage(summary.totalOrders?.percentageChange), icon: Inventory2, negative: (summary.totalOrders?.percentageChange ?? 0) < 0, style: 'bg-indigo-50 text-indigo-600'},
        { label: summary.poOpen?.title ?? "PO Open", value: String(summary.poOpen?.count ?? 0), trend: formatPercentage(summary.poOpen?.percentageChange), icon: InsertDriveFileOutlined, negative: (summary.poOpen?.percentageChange ?? 0) < 0, style: 'bg-blue-50 text-blue-600' },
        { label: summary.poApproved?.title ?? "PO Approved", value: String(summary.poApproved?.count ?? 0), trend: formatPercentage(summary.poApproved?.percentageChange), icon: FileDownloadDoneOutlined, negative: (summary.poApproved?.percentageChange ?? 0) < 0, style: 'bg-emerald-50 text-emerald-600' },
        { label: summary.poCompleted?.title ?? "PO Completed", value: String(summary.poCompleted?.count ?? 0), trend: formatPercentage(summary.poCompleted?.percentageChange), icon: TaskAlt, negative: (summary.poCompleted?.percentageChange ?? 0) < 0, style: 'bg-violet-50 text-violet-600' },
        { label: "Sales Status Breakdown", value: `${summary.salesStatus?.fulfillmentRate ?? 0}%`, trend: formatPercentage(summary.salesStatus?.percentageChange), icon: TrendingUp, negative: (summary.salesStatus?.percentageChange ?? 0) < 0, style: 'bg-orange-50 text-orange-600' },
    ] : [];

    const orderStatusData = (summary?.poStatusOverview ?? []).map((item, index) => ({
        name: item.label,
        value: item.percentage ?? 0,
        color: chartColors[index % chartColors.length],
    }));

    return (
        <main className="min-h-full px-2">
            <div className="w-full">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl text-left font-semibold tracking-tight text-slate-900 sm:text-3xl">
                            {loading ? (
                                <Skeleton animation="wave" variant="text" width={280} height={40} />
                            ) : (
                                `Welcome Back, ${summary?.userName ?? ""}`
                            )}
                        </h1>
                        {loading ? (
                            <Skeleton animation="wave" variant="text" width={340} height={22} sx={{ mt: 0.5 }} />
                        ) : (
                            <p className="mt-1 text-left text-sm text-slate-500">Here is what's happening with your {summary?.franchiseName ?? "Franchise ERP"} inventory and orders today.</p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                        <TextField
                            type="date"
                            size="small"
                            label="From Date"
                            value={fromDate}
                            onChange={(event) => handleFromDateChange(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: toDate || undefined }}
                            sx={{ width: 165, bgcolor: "white" }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To Date"
                            value={toDate}
                            onChange={(event) => handleToDateChange(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: fromDate || undefined }}
                            sx={{ width: 165, bgcolor: "white" }}
                        />
                    </div>
                </div>
                {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}

                {loading || ordersLoading ? <DashboardSkeleton /> : (
                    <>
                <section aria-label="Key metrics" className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    {metrics.map((metric) => (
                        <MetricCard key={metric.label} {...metric} />
                    ))}
                </section>

                <section className="mt-5 grid gap-4 lg:grid-cols-5">
                    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-3">
                        <h2 className="text-base font-semibold text-slate-900">Purchase Order Status Overview</h2>
                        <div className="mt-3 flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:justify-start">
                            <div className="relative h-36 w-36 shrink-0" aria-label="Purchase order status chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={orderStatusData} dataKey="value" innerRadius={44} outerRadius={64} paddingAngle={0} stroke="none">
                                            {orderStatusData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => [`${value}%`, "Orders"]} 
                                            wrapperStyle={{ zIndex: 1000, outline: "none" }}
                                            offset={16}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-xs text-slate-500">Total POs</span>
                                    <span className="text-xl font-semibold text-slate-900">{summary?.totalPoCount ?? 0}</span>
                                </div>
                            </div>
                            <ul className="space-y-2 text-sm text-slate-600">
                                {orderStatusData.map((item) => (
                                    <li key={item.name} className="flex items-center gap-2">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                                        {item.name} ({item.value}%)
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </article>

                    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
                        <h2 className="text-base font-semibold text-slate-900">ERP Quick Actions</h2>
                        <div className="mt-3 space-y-2">
                            <Link to="/create-order" className="flex h-9 w-full items-center gap-2 rounded-md border border-blue-600 px-3 text-left text-sm font-medium text-blue-600 transition hover:bg-blue-50">
                                <Add fontSize="small" />
                                Create New PO
                            </Link>

                            <Link to="/sales-order" className="flex h-9 w-full items-center gap-2 rounded-md border border-blue-600 px-3 text-left text-sm font-medium text-blue-600 transition hover:bg-blue-50">
                                <Assignment fontSize="small" />
                                View Sales Orders
                            </Link>

                            {/* <Link to="/warranty-requests" className="flex h-9 w-full items-center gap-2 rounded-md border border-blue-600 px-3 text-left text-sm font-medium text-blue-600 transition hover:bg-blue-50">
                                <ShieldOutlined fontSize="small" />
                                Check Warranty Requests
                            </Link> */}

                            {canTransferInventory && (
                                <Link to="/transfer-order" className="flex h-9 w-full items-center gap-2 rounded-md border border-blue-600 px-3 text-left text-sm font-medium text-blue-600 transition hover:bg-blue-50">
                                <SwapHoriz fontSize="small" />
                                Transfer Inventory
                                </Link>
                            )}
                        </div>
                    </article>
                </section>

                <section className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-4">
                        <h2 className="text-base font-semibold text-slate-900">Recent Orders Activity</h2>
                        {/* <button onClick={() => navigate("/history/order")} className="text-sm font-medium text-blue-600 hover:text-blue-800">View All Orders <span aria-hidden="true">→</span></button> */}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-[760px] w-full text-left text-sm">
                            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                                <tr>{["Order", "Type", "Vendor / Customer", "Date", "Status", "Amount"].map((heading) => <th key={heading} className="px-4 py-3">{heading}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-600">
                                {recentOrders.map((order) => (
                                    <tr key={order.id} className="hover:bg-slate-50">
                                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-blue-600">{order.orderNumber}</td>
                                        <td className="whitespace-nowrap px-4 py-3">{order.orderType}</td>
                                        <td className="px-4 py-3">{order.partyName || "--"}</td>
                                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">{order.dateFormatted || order.date || "--"}</td>
                                                                        <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[order.status?.toUpperCase() ?? ""] ?? "border-slate-200 bg-slate-50 text-slate-700"}`}>{order.statusDisplayName || order.status || "Unknown"}</span></td>
                                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-800">{formatAmount(order.amount)}</td>
                                    </tr>
                                ))}
                                {!ordersLoading && !error && recentOrders.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">No recent orders found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
                    </>
                )}
            </div>
        </main>
    );
}

export default ERPDashboard;
